import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { settingsAPI, authAPI, telegramAPI, emailAPI, pushAPI } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { User, Bell, Send, MessageCircle, Loader2, Palette, Sun, Moon, Monitor, Lock, Eye, EyeOff, Phone, Banknote, Save, Trash2, AlertTriangle, Link2, Unlink, CheckCircle2, ExternalLink, Clock, BellRing, TestTube, Smartphone } from 'lucide-react';

export default function SettingsPage() {
  const { user, logout, updateUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [settings, setSettings] = useState({
    email_enabled: false,
    telegram_enabled: false,
    push_enabled: false,
    telegram_chat_id: '',
    reminder_minutes: 30,
    notify_on_login: false,
    notify_on_new_order: false,
    notify_on_new_task: false,
    notify_on_overdue_task: false,
    daily_summary_enabled: false,
    weekly_summary_enabled: false,
    monthly_summary_enabled: false,
    daily_summary_hour: 9,
    daily_summary_minute: 0,
  });

  // Telegram linking states
  const [telegramLinked, setTelegramLinked] = useState(false);
  const [telegramChatId, setTelegramChatId] = useState(null);
  const [linkingTelegram, setLinkingTelegram] = useState(false);
  const [linkCode, setLinkCode] = useState(null);
  const [botLink, setBotLink] = useState(null);
  const [pollingLink, setPollingLink] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [sendingTestPush, setSendingTestPush] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [subscribingPush, setSubscribingPush] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);

  // Profile edit states
  const [profileData, setProfileData] = useState({
    name: '',
    phone: '',
    currency: 'RUB',
  });

  const CURRENCIES = [
    { code: 'RUB', symbol: '₽', label: 'Рубль (₽)' },
    { code: 'USD', symbol: '$', label: 'Доллар ($)' },
    { code: 'EUR', symbol: '€', label: 'Евро (€)' },
    { code: 'KZT', symbol: '₸', label: 'Тенге (₸)' },
  ];
  
  // Password change states
  const [changingPassword, setChangingPassword] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Delete account states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Order number settings
  const [orderNumberSettings, setOrderNumberSettings] = useState({
    prefix: '',
    start_number: 1,
    digit_count: 1,
    suffix: '',
  });
  const [savingOrderNumber, setSavingOrderNumber] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        phone: user.phone || '',
        currency: user.currency || 'RUB',
      });
    }
  }, [user]);

  const loadSettings = async () => {
    try {
      const res = await settingsAPI.getNotifications();
      setSettings({
        email_enabled: res.data.email_enabled || false,
        telegram_enabled: res.data.telegram_enabled || false,
        push_enabled: res.data.push_enabled || false,
        telegram_chat_id: res.data.telegram_chat_id || '',
        reminder_minutes: res.data.reminder_minutes || 30,
        notify_on_login: res.data.notify_on_login || false,
        notify_on_new_order: res.data.notify_on_new_order || false,
        notify_on_new_task: res.data.notify_on_new_task || false,
        notify_on_overdue_task: res.data.notify_on_overdue_task || false,
        daily_summary_enabled: res.data.daily_summary_enabled || false,
        weekly_summary_enabled: res.data.weekly_summary_enabled || false,
        monthly_summary_enabled: res.data.monthly_summary_enabled || false,
        daily_summary_hour: res.data.daily_summary_hour ?? 9,
        daily_summary_minute: res.data.daily_summary_minute ?? 0,
      });
    } catch (error) {
      // Settings might not exist yet
    }
    try {
      const onRes = await settingsAPI.getOrderNumber();
      setOrderNumberSettings({
        prefix: onRes.data.prefix || '',
        start_number: onRes.data.start_number ?? 1,
        digit_count: onRes.data.digit_count ?? 1,
        suffix: onRes.data.suffix || '',
      });
    } catch (error) {
      // Order number settings might not exist yet
    }
    setLoading(false);
  };

  // Load Telegram link status
  const loadTelegramStatus = useCallback(async () => {
    try {
      const res = await telegramAPI.getLinkStatus();
      setTelegramLinked(res.data.linked);
      setTelegramChatId(res.data.chat_id);
    } catch (error) {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadTelegramStatus();
  }, [loadTelegramStatus]);

  // Check push subscription status on mount
  useEffect(() => {
    const checkPushStatus = async () => {
      try {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
          setPushSupported(true);
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setPushSubscribed(!!subscription);
        }
      } catch (e) {
        // Push not supported
      }
    };
    checkPushStatus();
  }, []);

  // Register service worker on app load
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  // Subscribe to push notifications
  const handleEnablePush = async () => {
    // Check browser support
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast.error('Ваш браузер не поддерживает push-уведомления. Попробуйте обновить браузер или используйте Chrome/Firefox.');
      setSettings(prev => ({ ...prev, push_enabled: false }));
      return;
    }

    setSubscribingPush(true);
    try {
      // Register service worker if not yet
      await navigator.serviceWorker.register('/sw.js');

      // 1. Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Разрешение на уведомления отклонено. Разрешите в настройках браузера.');
        setSettings(prev => ({ ...prev, push_enabled: false }));
        setSubscribingPush(false);
        return;
      }

      // 2. Get VAPID public key
      const vapidRes = await pushAPI.getVapidKey();
      const publicKey = vapidRes.data.public_key;

      // 3. Convert VAPID key to Uint8Array
      const padding = '='.repeat((4 - publicKey.length % 4) % 4);
      const base64 = (publicKey + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      const applicationServerKey = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; i++) {
        applicationServerKey[i] = rawData.charCodeAt(i);
      }

      // 4. Subscribe via PushManager
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // 5. Send subscription to backend
      await pushAPI.subscribe(subscription.toJSON());
      setPushSubscribed(true);
      setSettings(prev => ({ ...prev, push_enabled: true }));
      toast.success('Push-уведомления включены!');
    } catch (error) {
      console.error('Push subscribe error:', error);
      toast.error('Не удалось подключить push-уведомления');
      setSettings(prev => ({ ...prev, push_enabled: false }));
    } finally {
      setSubscribingPush(false);
    }
  };

  // Unsubscribe from push notifications
  const handleDisablePush = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await pushAPI.unsubscribe(subscription.endpoint);
        await subscription.unsubscribe();
      }
      setPushSubscribed(false);
      setSettings(prev => ({ ...prev, push_enabled: false }));
      toast.success('Push-уведомления отключены');
    } catch (error) {
      toast.error('Ошибка отключения push');
    }
  };

  // Toggle push
  const handlePushToggle = (checked) => {
    if (checked) {
      handleEnablePush();
    } else {
      handleDisablePush();
    }
  };

  // Send test push
  const handleSendTestPush = async () => {
    setSendingTestPush(true);
    try {
      await pushAPI.testMessage();
      toast.success('Тестовое push-уведомление отправлено');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка отправки');
    } finally {
      setSendingTestPush(false);
    }
  };

  // Generate Telegram linking code
  const handleGenerateLink = async () => {
    setLinkingTelegram(true);
    try {
      const res = await telegramAPI.generateLink();
      setLinkCode(res.data.code);
      setBotLink(res.data.bot_link);
      setPollingLink(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка генерации ссылки');
    } finally {
      setLinkingTelegram(false);
    }
  };

  // Poll for link status when waiting for user to link
  useEffect(() => {
    if (!pollingLink) return;
    
    const interval = setInterval(async () => {
      try {
        const res = await telegramAPI.getLinkStatus();
        if (res.data.linked) {
          setTelegramLinked(true);
          setTelegramChatId(res.data.chat_id);
          setPollingLink(false);
          setLinkCode(null);
          setBotLink(null);
          // Update settings with the chat_id
          setSettings(prev => ({ ...prev, telegram_chat_id: res.data.chat_id, telegram_enabled: true }));
          toast.success('Telegram успешно привязан!');
        }
      } catch (error) {
        // ignore
      }
    }, 3000); // check every 3 seconds

    // Stop polling after 10 minutes
    const timeout = setTimeout(() => {
      setPollingLink(false);
      setLinkCode(null);
      setBotLink(null);
    }, 10 * 60 * 1000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [pollingLink]);

  // Unlink Telegram
  const handleUnlinkTelegram = async () => {
    setUnlinking(true);
    try {
      await telegramAPI.unlink();
      setTelegramLinked(false);
      setTelegramChatId(null);
      setSettings(prev => ({ ...prev, telegram_chat_id: '', telegram_enabled: false }));
      toast.success('Telegram отвязан');
    } catch (error) {
      toast.error('Ошибка отвязки');
    } finally {
      setUnlinking(false);
    }
  };

  // Send test message
  const handleSendTestMessage = async () => {
    setSendingTest(true);
    try {
      await telegramAPI.testMessage();
      toast.success('Тестовое сообщение отправлено в Telegram');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка отправки');
    } finally {
      setSendingTest(false);
    }
  };

  // Send test email
  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      await emailAPI.testMessage();
      toast.success('Тестовое письмо отправлено на ваш email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка отправки email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Automatically include user's timezone offset for correct notification timing
      const dataToSave = {
        ...settings,
        timezone_offset: new Date().getTimezoneOffset(),  // e.g., -180 for UTC+3
      };
      await settingsAPI.updateNotifications(dataToSave);
      toast.success('Настройки сохранены');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const saveOrderNumberSettings = async () => {
    setSavingOrderNumber(true);
    try {
      await settingsAPI.updateOrderNumber(orderNumberSettings);
      toast.success('Настройки нумерации сохранены');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSavingOrderNumber(false);
    }
  };

  const getOrderNumberPreview = () => {
    const { prefix, start_number, digit_count, suffix } = orderNumberSettings;
    const num = String(start_number || 1).padStart(Math.max(digit_count || 1, 1), '0');
    const displayPrefix = prefix || '№';
    return `${displayPrefix}${num}${suffix || ''}`;
  };

  const saveProfile = async () => {
    if (!profileData.name.trim()) {
      toast.error('Имя не может быть пустым');
      return;
    }
    setSavingProfile(true);
    try {
      const res = await authAPI.updateProfile({
        name: profileData.name.trim(),
        phone: profileData.phone.trim(),
        currency: profileData.currency,
      });
      updateUser(res.data);
      toast.success('Профиль обновлён');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка сохранения профиля');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Заполните все поля');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error('Новый пароль должен содержать минимум 8 символов');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Пароли не совпадают');
      return;
    }
    
    setChangingPassword(true);
    try {
      await authAPI.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });
      toast.success('Пароль успешно изменён');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка смены пароля');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSetPassword = async () => {
    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Заполните все поля');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error('Пароль должен содержать минимум 8 символов');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Пароли не совпадают');
      return;
    }
    
    setSettingPassword(true);
    try {
      await authAPI.setPassword({
        new_password: passwordData.newPassword,
      });
      toast.success('Пароль установлен! Теперь вы можете входить через email и пароль.');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      // Update user state to reflect has_password = true
      if (user) {
        updateUser({ ...user, has_password: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка установки пароля');
    } finally {
      setSettingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'Delete') {
      toast.error("Введите 'Delete' для подтверждения");
      return;
    }
    setDeleting(true);
    try {
      await authAPI.deleteAccount({ confirmation: 'Delete' });
      toast.success('Аккаунт удалён');
      logout();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка удаления аккаунта');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-lg font-semibold tracking-tight text-foreground">Настройки</h1>
        <p className="text-sm text-muted-foreground">Управление аккаунтом и уведомлениями</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Left Column: Profile, Password, Theme */}
        <div className="space-y-6">

      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Профиль
          </CardTitle>
          <CardDescription>Информация о вашем аккаунте</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="profile-name">Имя</Label>
              <Input
                id="profile-name"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                placeholder="Ваше имя"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={user?.email || ''} disabled className="bg-muted" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="profile-phone" className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" />
                Телефон
              </Label>
              <Input
                id="profile-phone"
                type="tel"
                value={profileData.phone}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Allow only digits, +, spaces, dashes, parentheses
                  const cleaned = raw.replace(/[^\d+\s\-()]/g, '');
                  // Auto-format Russian numbers: +7 → +7 (XXX) XXX-XX-XX
                  const digits = cleaned.replace(/\D/g, '');
                  if (digits.length === 0 && !cleaned.startsWith('+')) {
                    setProfileData({ ...profileData, phone: '' });
                    return;
                  }
                  let formatted = cleaned;
                  if (digits.length >= 1 && (cleaned.startsWith('+7') || cleaned.startsWith('8') || cleaned.startsWith('7'))) {
                    const d = cleaned.startsWith('+7') ? digits.slice(1) : (cleaned.startsWith('8') || cleaned.startsWith('7')) ? digits.slice(1) : digits;
                    formatted = '+7';
                    if (d.length > 0) formatted += ' (' + d.slice(0, 3);
                    if (d.length >= 3) formatted += ') ';
                    if (d.length > 3) formatted += d.slice(3, 6);
                    if (d.length > 6) formatted += '-' + d.slice(6, 8);
                    if (d.length > 8) formatted += '-' + d.slice(8, 10);
                  }
                  setProfileData({ ...profileData, phone: formatted });
                }}
                placeholder="+7 (999) 123-45-67"
                maxLength={18}
              />
            </div>
          </div>

          <Separator />

          {/* Currency */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Banknote className="h-3.5 w-3.5" />
              Валюта
            </Label>
            <div className="flex gap-1.5">
              {CURRENCIES.map((cur) => (
                <Button
                  key={cur.code}
                  type="button"
                  variant={profileData.currency === cur.code ? 'default' : 'outline'}
                  size="sm"
                  className="px-3"
                  onClick={() => setProfileData({ ...profileData, currency: cur.code })}
                >
                  <span className="font-bold mr-1">{cur.symbol}</span>
                  <span className="text-xs">{cur.code}</span>
                </Button>
              ))}
            </div>
          </div>

          <Button onClick={saveProfile} disabled={savingProfile} className="w-full sm:w-auto">
            {savingProfile ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Сохранение...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Сохранить профиль
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Change Password / Set Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            {user?.has_password === false ? 'Установить пароль' : 'Смена пароля'}
          </CardTitle>
          <CardDescription>
            {user?.has_password === false 
              ? 'Вы вошли через OAuth. Установите пароль, чтобы также входить по email и паролю.' 
              : 'Измените пароль для входа в систему'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Only show current password field if user has a password */}
          {user?.has_password !== false && (
            <div className="space-y-2">
              <Label htmlFor="current-password">Текущий пароль</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Введите текущий пароль"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="new-password">{user?.has_password === false ? 'Пароль' : 'Новый пароль'}</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                placeholder="Минимум 8 символов"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Подтвердите пароль</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder="Повторите пароль"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <Button
            onClick={user?.has_password === false ? handleSetPassword : handleChangePassword}
            disabled={changingPassword || settingPassword}
            className="w-full sm:w-auto"
          >
            {(changingPassword || settingPassword) ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {user?.has_password === false ? 'Установка...' : 'Изменение...'}
              </>
            ) : (
              user?.has_password === false ? 'Установить пароль' : 'Изменить пароль'
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Theme */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Внешний вид
          </CardTitle>
          <CardDescription>Настройте тему оформления</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-1.5">
            <Button
              variant={theme === 'light' ? 'default' : 'outline'}
              size="sm"
              className="gap-1.5 px-3"
              onClick={() => setTheme('light')}
              data-testid="theme-light-btn"
            >
              <Sun className="h-4 w-4" />
              Светлая
            </Button>
            <Button
              variant={theme === 'dark' ? 'default' : 'outline'}
              size="sm"
              className="gap-1.5 px-3"
              onClick={() => setTheme('dark')}
              data-testid="theme-dark-btn"
            >
              <Moon className="h-4 w-4" />
              Тёмная
            </Button>
            <Button
              variant={theme === 'system' ? 'default' : 'outline'}
              size="sm"
              className="gap-1.5 px-3"
              onClick={() => setTheme('system')}
              data-testid="theme-system-btn"
            >
              <Monitor className="h-4 w-4" />
              Авто
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="text-destructive flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Опасная зона
          </CardTitle>
          <CardDescription>Необратимые действия</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Button variant="destructive" onClick={logout} data-testid="logout-settings-btn">
            Выйти из аккаунта
          </Button>

          <Separator />

          <div className="space-y-3">
            <div>
              <p className="font-medium text-destructive">Удалить аккаунт</p>
              <p className="text-sm text-muted-foreground">
                Все данные будут безвозвратно удалены: заказы, клиенты, задачи, формы и материалы.
              </p>
            </div>

            {!showDeleteConfirm ? (
              <Button
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Удалить аккаунт
              </Button>
            ) : (
              <div className="space-y-3 p-4 rounded-lg border border-destructive/50 bg-destructive/5">
                <p className="text-sm font-medium">
                  Введите <span className="font-mono font-bold text-destructive">Delete</span> для подтверждения:
                </p>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Delete"
                  className="max-w-xs"
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    onClick={handleDeleteAccount}
                    disabled={deleting || deleteConfirmText !== 'Delete'}
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Удаление...
                      </>
                    ) : (
                      <>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Подтвердить удаление
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                    }}
                  >
                    Отмена
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

        </div>
        {/* Right Column: Notifications */}
        <div className="space-y-6">

      {/* Order Number Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-lg">#</span>
            Нумерация заказов
          </CardTitle>
          <CardDescription>Настройте формат номеров заказов</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Preview */}
          <div className="p-4 rounded-lg bg-muted/50 border border-border text-center">
            <p className="text-xs text-muted-foreground mb-1">Пример номера</p>
            <p className="text-2xl font-bold font-mono text-primary">{getOrderNumberPreview()}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Префикс</Label>
              <Input
                value={orderNumberSettings.prefix}
                onChange={(e) => setOrderNumberSettings({ ...orderNumberSettings, prefix: e.target.value })}
                placeholder="ЗК-"
                maxLength={10}
              />
              <p className="text-xs text-muted-foreground">Текст перед номером (по умолчанию №)</p>
            </div>
            <div className="space-y-2">
              <Label>Суффикс</Label>
              <Input
                value={orderNumberSettings.suffix}
                onChange={(e) => setOrderNumberSettings({ ...orderNumberSettings, suffix: e.target.value })}
                placeholder="/2026"
                maxLength={10}
              />
              <p className="text-xs text-muted-foreground">Текст после номера</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Начать с номера</Label>
              <Input
                type="number"
                min={1}
                value={orderNumberSettings.start_number}
                onChange={(e) => {
                  const val = e.target.value;
                  setOrderNumberSettings({ ...orderNumberSettings, start_number: val === '' ? '' : Math.max(1, parseInt(val) || 1) });
                }}
                onBlur={() => {
                  if (!orderNumberSettings.start_number || orderNumberSettings.start_number < 1) {
                    setOrderNumberSettings({ ...orderNumberSettings, start_number: 1 });
                  }
                }}
              />
              <p className="text-xs text-muted-foreground">Новые заказы начнутся с этого номера</p>
            </div>
            <div className="space-y-2">
              <Label>Количество цифр</Label>
              <Select
                value={String(orderNumberSettings.digit_count)}
                onValueChange={(val) => setOrderNumberSettings({ ...orderNumberSettings, digit_count: parseInt(val) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Без нулей (1, 2, 3)</SelectItem>
                  <SelectItem value="2">2 цифры (01, 02)</SelectItem>
                  <SelectItem value="3">3 цифры (001, 002)</SelectItem>
                  <SelectItem value="4">4 цифры (0001, 0002)</SelectItem>
                  <SelectItem value="5">5 цифр (00001, 00002)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Дополнение нулями слева</p>
            </div>
          </div>

          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
            <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
              💡 Можно менять стартовый номер в любое время. Если номер уже занят существующим заказом — система автоматически пропустит его и возьмёт ближайший свободный. Дубликатов не будет.
            </p>
          </div>

          <Button onClick={saveOrderNumberSettings} disabled={savingOrderNumber} className="w-full">
            {savingOrderNumber && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Save className="h-4 w-4 mr-2" />
            Сохранить нумерацию
          </Button>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Уведомления
          </CardTitle>
          <CardDescription>Настройте каналы и параметры уведомлений</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-muted">
                  <Send className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-medium">Email уведомления</p>
                  <p className="text-sm text-muted-foreground">
                    Получать уведомления на email
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.email_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, email_enabled: checked })}
                data-testid="email-notifications-switch"
              />
            </div>
            {settings.email_enabled && (
              <div className="ml-11 space-y-3">
                <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Email подключён</p>
                    <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70">{user?.email}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendTestEmail}
                  disabled={sendingTestEmail}
                  className="gap-1.5"
                  data-testid="email-test-btn"
                >
                  {sendingTestEmail ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Тестовое письмо
                </Button>
              </div>
            )}
          </div>

          {/* Push Notifications Section */}
          <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
                    <Smartphone className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="font-medium">Push-уведомления</p>
                    <p className="text-sm text-muted-foreground">
                      Уведомления в браузере и на рабочем столе
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.push_enabled}
                  onCheckedChange={handlePushToggle}
                  disabled={subscribingPush}
                  data-testid="push-notifications-switch"
                />
              </div>
              {settings.push_enabled && pushSubscribed && (
                <div className="ml-11 space-y-3">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <CheckCircle2 className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-amber-700 dark:text-amber-300">Push подключён</p>
                      <p className="text-xs text-amber-600/70 dark:text-amber-400/70">Уведомления будут приходить в этот браузер</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSendTestPush}
                    disabled={sendingTestPush}
                    className="gap-1.5"
                    data-testid="push-test-btn"
                  >
                    {sendingTestPush ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />}
                    Тестовое уведомление
                  </Button>
                </div>
              )}
              {subscribingPush && (
                <div className="ml-11">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <p className="text-sm">Подключение push-уведомлений...</p>
                  </div>
                </div>
              )}
            </div>

          <Separator />

          {/* Event Notification Toggles */}
          <div className="space-y-1">
            <div className="flex items-center gap-2 mb-3">
              <BellRing className="h-4 w-4 text-muted-foreground" />
              <p className="font-medium text-sm text-muted-foreground">Уведомлять о событиях</p>
            </div>

            {/* Notify on Login */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
                  <Lock className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="font-medium">Вход в панель</p>
                  <p className="text-sm text-muted-foreground">
                    Уведомление при каждом входе в систему
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.notify_on_login}
                onCheckedChange={(checked) => setSettings({ ...settings, notify_on_login: checked })}
                data-testid="notify-on-login-switch"
              />
            </div>

            {/* Notify on New Order */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-orange-100 dark:bg-orange-900/30">
                  <Bell className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="font-medium">Создание нового заказа</p>
                  <p className="text-sm text-muted-foreground">
                    Уведомление при создании нового заказа
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.notify_on_new_order}
                onCheckedChange={(checked) => setSettings({ ...settings, notify_on_new_order: checked })}
                data-testid="notify-on-new-order-switch"
              />
            </div>

            {/* Notify on New Task */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-purple-100 dark:bg-purple-900/30">
                  <CheckCircle2 className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="font-medium">Создание новой задачи</p>
                  <p className="text-sm text-muted-foreground">
                    Уведомление при создании новой задачи
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.notify_on_new_task}
                onCheckedChange={(checked) => setSettings({ ...settings, notify_on_new_task: checked })}
                data-testid="notify-on-new-task-switch"
              />
            </div>

            {/* Notify on Overdue Task */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30">
                  <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="font-medium">Просроченная задача</p>
                  <p className="text-sm text-muted-foreground">
                    Уведомление когда дедлайн задачи прошёл
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.notify_on_overdue_task}
                onCheckedChange={(checked) => setSettings({ ...settings, notify_on_overdue_task: checked })}
                data-testid="notify-on-overdue-task-switch"
              />
            </div>
          </div>

          <Separator />

          {/* Summary Section */}
          <div className="space-y-4">
            <div className="space-y-1 mb-2">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium text-muted-foreground">Сводки</p>
              </div>
            </div>

            {/* Daily Summary */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-indigo-100 dark:bg-indigo-900/30">
                  <Clock className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <p className="font-medium">Ежедневная сводка</p>
                  <p className="text-sm text-muted-foreground">
                    Заказы, задачи и доход за день
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.daily_summary_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, daily_summary_enabled: checked })}
                data-testid="daily-summary-switch"
              />
            </div>

            {/* Weekly Summary */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
                  <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="font-medium">Еженедельная сводка</p>
                  <p className="text-sm text-muted-foreground">
                    Итоги недели каждый понедельник
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.weekly_summary_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, weekly_summary_enabled: checked })}
                data-testid="weekly-summary-switch"
              />
            </div>

            {/* Monthly Summary */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-purple-100 dark:bg-purple-900/30">
                  <Clock className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="font-medium">Ежемесячная сводка</p>
                  <p className="text-sm text-muted-foreground">
                    Итоги месяца 1-го числа
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.monthly_summary_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, monthly_summary_enabled: checked })}
                data-testid="monthly-summary-switch"
              />
            </div>

            {/* Time selector — shown when any summary is enabled */}
            {(settings.daily_summary_enabled || settings.weekly_summary_enabled || settings.monthly_summary_enabled) && (
              <div className="ml-11 space-y-3">
                <div>
                  <label className="text-sm font-medium block mb-2">Время отправки:</label>
                  <input
                    type="time"
                    value={`${String(settings.daily_summary_hour).padStart(2, '0')}:${String(settings.daily_summary_minute).padStart(2, '0')}`}
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(':').map(Number);
                      setSettings({ ...settings, daily_summary_hour: h || 0, daily_summary_minute: m || 0 });
                    }}
                    className="w-32 h-10 px-3 text-base font-medium rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    data-testid="summary-time-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Сводки включают: заказы, задачи, клиенты, доходы/расходы, прибыль
                </p>
              </div>
            )}
          </div>

          <Separator />

          {/* Telegram Section */}
          <div className="space-y-4">
            {/* Telegram Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
                  <MessageCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="font-medium">Telegram уведомления</p>
                  <p className="text-sm text-muted-foreground">
                    Получать напоминания о задачах и заказах
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.telegram_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, telegram_enabled: checked })}
                disabled={!telegramLinked}
                data-testid="telegram-notifications-switch"
              />
            </div>

            {/* Telegram Link Status */}
            <div className="ml-11 space-y-3">
              {telegramLinked ? (
                <>
                  {/* Linked state */}
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Telegram привязан</p>
                      <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70">Chat ID: {telegramChatId}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSendTestMessage}
                      disabled={sendingTest}
                      className="gap-1.5"
                    >
                      {sendingTest ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BellRing className="h-3.5 w-3.5" />}
                      Тестовое уведомление
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUnlinkTelegram}
                      disabled={unlinking}
                      className="gap-1.5 text-destructive hover:text-destructive"
                    >
                      {unlinking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unlink className="h-3.5 w-3.5" />}
                      Отвязать
                    </Button>
                  </div>
                </>
              ) : linkCode && botLink ? (
                <>
                  {/* Linking in progress */}
                  <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 space-y-3">
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5">
                        <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Ожидание привязки...</p>
                        <p className="text-xs text-blue-600/80 dark:text-blue-400/80 mt-1">
                          Нажмите кнопку ниже, чтобы перейти к боту. Бот автоматически привяжет ваш аккаунт.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded bg-white dark:bg-background border">
                      <code className="text-sm font-mono font-bold flex-1 text-center">{linkCode}</code>
                    </div>
                    <Button
                      asChild
                      className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <a href={botLink} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                        Открыть бота в Telegram
                      </a>
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                      Код действителен 10 минут
                    </p>
                  </div>
                </>
              ) : (
                <>
                  {/* Not linked */}
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border border-dashed">
                    <Link2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      Привяжите Telegram для получения уведомлений
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateLink}
                    disabled={linkingTelegram}
                    className="gap-1.5"
                  >
                    {linkingTelegram ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
                    Привязать Telegram
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Reminder Time Selector - when telegram OR email enabled */}
          {((settings.telegram_enabled && telegramLinked) || settings.email_enabled || settings.push_enabled) && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <Label className="font-medium">Время напоминания</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  За сколько до дедлайна задачи или срока заказа вы хотите получить уведомление. Напоминание придёт во все включённые каналы (Email, Telegram, Push).
                </p>
                <Select
                  value={String(settings.reminder_minutes)}
                  onValueChange={(val) => setSettings({ ...settings, reminder_minutes: parseInt(val, 10) })}
                >
                  <SelectTrigger className="w-full sm:w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">За 5 минут</SelectItem>
                    <SelectItem value="10">За 10 минут</SelectItem>
                    <SelectItem value="15">За 15 минут</SelectItem>
                    <SelectItem value="30">За 30 минут (по умолчанию)</SelectItem>
                    <SelectItem value="60">За 1 час</SelectItem>
                    <SelectItem value="120">За 2 часа</SelectItem>
                    <SelectItem value="360">За 6 часов</SelectItem>
                    <SelectItem value="720">За 12 часов</SelectItem>
                    <SelectItem value="1440">За 24 часа (за сутки)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Пример: если у задачи дедлайн 15:00 и выбрано «За 1 час» — уведомление придёт в 14:00
                </p>
              </div>
            </>
          )}

          <Button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Сохранить настройки
          </Button>
        </CardContent>
      </Card>

        </div>
      </div>
    </div>
  );
}
